import {
 collection,
 addDoc,
 getDocs,
 query,
 where,
 Timestamp,
 serverTimestamp,
 doc,
 updateDoc,
 increment,
} from "firebase/firestore";
import { databasePromise } from "configuration";
import { getBrowser, getDeviceId } from "utilities";

// Generates a stable daily deduplication key without leaking user data to
// third-party IP lookup APIs. Uses deviceId (stored in localStorage) + userId
// + targetId + today's date. Anonymous users are keyed by deviceId only.
const getDedupKey = (targetId, userId) => {
 const deviceId = getDeviceId();
 const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
 return `${deviceId}-${userId || "anon"}-${targetId}-${today}`;
};

export const analyticsService = {
 /**
  * Tracks a view for a deal or restaurant.
  * - Deduplicates by deviceId + userId per day (no external API calls)
  * - Increments a denormalized `viewCount` field on the target document (for fast sort)
  *
  * @param {"deal" | "restaurant"} type
  * @param {string} targetId - The deal or restaurant ID
  * @param {string | null} userId - The current user's UID, or null if anonymous
  */
 trackView: async (type, targetId, userId = null) => {
  try {
   const db = await databasePromise;
   const userAgent = navigator.userAgent;
   const browser = getBrowser(userAgent);
   const deviceType = /Mobi|Android/i.test(userAgent) ? "mobile" : "desktop";
   const referrer = document.referrer || null;
   const dedupKey = getDedupKey(targetId, userId);

   const todayStart = new Date();
   todayStart.setHours(0, 0, 0, 0);

   const viewsRef = collection(db, "views");
   const existingView = await getDocs(
    query(
     viewsRef,
     where("type", "==", type),
     where("targetId", "==", targetId),
     where("dedupKey", "==", dedupKey),
     where("timestamp", ">=", Timestamp.fromDate(todayStart)),
    ),
   );

   if (existingView.empty) {
    // Write raw event document for analytics
    await addDoc(viewsRef, {
     type,
     targetId,
     userId,
     dedupKey,
     userAgent,
     browser,
     deviceType,
     referrer,
     timestamp: serverTimestamp(),
    });

    // Increment denormalized counter on the target document
    const collectionName = type === "deal" ? "deals" : "restaurants";
    const targetRef = doc(db, collectionName, targetId);
    await updateDoc(targetRef, { viewCount: increment(1) });
   }
  } catch (err) {
   console.error(`Failed to track ${type} view:`, err);
  }
 },
};
